import packageConfig from '../package.json'
import { Platform } from '@monogrid/js-utils'
import availableLanguages from '@/assets/data/availableLanguages.json'
import CMSAuth from './services/CMSAuth'
import { userHasRequestedACL, EDITOR_ACL } from './services/AuthLevel'
// import Vue from 'vue'

//
// ROUTING GUARDS
//

/**
 * restricts route to only logged users regardless of Accessl Level Rights
 *
 * @param {*} toRoute
 * @param {*} fromRoute
 * @param {*} next
 */
const onlyLoggedUsersGuard = async (toRoute, fromRoute, next) => {
  // console.log('only LOGGED users guard')
  const user = await CMSAuth.getLoggedUser()
  if (user) {
    next()
  } else {
    next({ name: 'Onboarding', params: { language: 'en' } })
  }
}
/**
 * restricts route to only UNLOGGED users, logged users are redirected home
 *
 * @param {*} toRoute
 * @param {*} fromRoute
 * @param {*} next
 */
const onlyUnLoggedUsersGuard = async (toRoute, fromRoute, next) => {
  // console.log('only unlogged users guard')
  const user = await CMSAuth.getLoggedUser()
  if (!user) {
    next()
  } else {
    next({ name: 'Home', params: { language: 'en' } })
  }
}
/**
 * Restrict route to a minumum ACL, unauthorized users see nothing
 *
 * @param {*} minAuthLevel
 */
const aclGuard = (minAuthLevel) => async (toRoute, fromRoute, next) => {
  let user
  try {
    user = await CMSAuth.getLoggedUser()
  } catch (e) {
    next({ name: 'Login', params: { language: 'en' } })
  }

  if (userHasRequestedACL(user, minAuthLevel)) {
    next()
  } else {
    next(new Error('UNAUTHORIZED'))
  }
}

//
// only pages should be imported and used here
// http://bradfrost.com/blog/post/atomic-web-design/
//
let routes = [
  {
    path: '/',
    // name: 'Home',
    beforeEnter: onlyLoggedUsersGuard,
    meta: {
      onlyLoggedUsers: true
    },
    component: () => import(/* webpackChunkName: "page-home" */ './pages/HomePage'),
    children: [
      {
        path: '',
        name: 'Home',
        props: true,
        component: () => import(/* webpackChunkName: "page-landing" */ './pages/RegisteredLandingPage'),
        meta: {
          noBackground: true
        }
      },
      {
        path: 'room/:slug',
        name: 'Live',
        props: true,
        component: () => import(/* webpackChunkName: "page-livestream" */ './pages/LiveStreamPage'),
        meta: {
          noBackground: true
        }
      },
      {
        path: 'recording/:type/:slug',
        name: 'EventRecording',
        props: true,
        component: () => import(/* webpackChunkName: "page-event-recording" */ './pages/RecordingPage')
      },
      {
        path: 'calendar/:date?/:talk?',
        name: 'Calendar',
        props: true,
        meta: {
          noBackground: true
        },
        component: () => import(/* webpackChunkName: "page-calendar" */ './pages/CalendarPage')
      },
      {
        path: 'search',
        name: 'Search',
        props: true,
        component: () => import(/* webpackChunkName: "page-search" */ './pages/SearchPage')
      },
      {
        path: 'workshops',
        name: 'WorkshopsList',
        props: true,
        component: () => import(/* webpackChunkName: "page-workshops" */ './pages/WorkshopListPage'),
        meta: {
          noBackground: true
        }
      },
      {
        path: 'suggested',
        name: 'Suggested',
        props: true,
        component: () => import(/* webpackChunkName: "page-suggested-events" */ './organisms/SuggestedEvents'),
        meta: {
          noBackground: true
        }
      },
      {
        path: 'favorites',
        name: 'Favorites',
        props: true,
        component: () => import(/* webpackChunkName: "page-suggested-events" */ './organisms/FavoritesEvents'),
        meta: {
          noBackground: true
        }
      }
      // {
      //   path: 'talk/:slug',
      //   name: 'Talk',
      //   props: true,
      //   meta: {
      //     darkBackground: true
      //   },
      //   component: () => import(/* webpackChunkName: "page-talk" */ './pages/TalkPage')
      // },
      // {
      //   path: 'workshops/:slug',
      //   name: 'Workshop',
      //   props: true,
      //   meta: {
      //     darkBackground: true
      //   },
      //   component: () => import(/* webpackChunkName: "page-talk" */ './pages/WorkshopPage')
      // }
    ]
  },
  {
    path: '/onboarding',
    name: 'Onboarding',
    beforeEnter: onlyUnLoggedUsersGuard,
    props: true,
    meta: {
      onlyUnloggedUsers: true
    },
    component: () => import(/* webpackChunkName: "page-onboarding" */ './pages/OnboardingPage')
  },
  {
    path: '/register',
    name: 'Register',
    beforeEnter: onlyUnLoggedUsersGuard,
    meta: {
      onlyUnloggedUsers: true
    },
    component: () => import(/* webpackChunkName: "page-register" */ './pages/RegisterPage')
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    beforeEnter: onlyUnLoggedUsersGuard,
    meta: {
      onlyUnloggedUsers: true
    },
    component: () => import(/* webpackChunkName: "page-forgot-password" */ './pages/ForgotPasswordPage')
  },
  {
    path: '/user-management',
    name: 'UserManagement',
    beforeEnter: aclGuard(EDITOR_ACL),
    meta: {
      onlyUnloggedUsers: true
    },
    component: () => import(/* webpackChunkName: "page-user-management" */ './pages/RestrictedUserManagement')
  },
  {
    path: '/workshop-management',
    name: 'WorkshopManagement',
    beforeEnter: aclGuard(EDITOR_ACL),
    meta: {
      onlyUnloggedUsers: true
    },
    component: () => import(/* webpackChunkName: "page-workshop-management" */ './pages/RestrictedWorkshopManagement')
  }
]

//
// System routes
//
if (!packageConfig.multilanguage || packageConfig.multilanguage === 'false') {
  // no multilanguage
  routes = routes.concat([
    {
      // matches /anyroute (if not found) and redirects it to /
      path: '*',
      redirect: '/'
    }
  ])
} else {
  //
  // multilanguage system
  // don't touch this :)
  //

  // create a regex capturing group like en|it|es|ru
  // based on availableLanguages
  let possibleLanguages = ''
  Object.keys(availableLanguages).forEach((lang) => {
    possibleLanguages += lang + '|'
  })
  possibleLanguages = possibleLanguages.substring(0, possibleLanguages.lastIndexOf('|'))

  // determine user browser language
  let userLanguage
  if (!Platform.prerenderer) {
    userLanguage = (window.navigator.languages ? window.navigator.languages[0] : null) || window.navigator.userLanguage || window.navigator.language || 'en'
    // strip down '-US' part (as in 'en-US' or similar) if present
    userLanguage = userLanguage.split('-')[0]
  } else {
    userLanguage = 'en'
  }

  const getValidLanguage = () => {
    // if it exist, set user language as default
    if (availableLanguages[userLanguage]) {
      return userLanguage
    }
    // set first language as default
    return Object.keys(availableLanguages)[0]
  }

  routes.forEach(route => {
    const oldPath = route.path
    // modify routes to require a valid language before it
    route.path = `/:language(${possibleLanguages})${route.path}`

    if (Platform.prerenderer) {
      // create duplcate routes for pre-renderer with no language
      // it needs them in order to avoid redirection from / to /en (for example)

      // TODO: Deep clone of nested routes?
      const dup = {}
      Object.keys(route).forEach(key => {
        dup[key] = route[key]
      })
      dup.name = route.name + '-prerenderer'
      dup.path = oldPath
      routes.push(dup)
    }
  })
  // append utility routes for redirection
  routes = routes.concat([
    {
      // matches /validlanguage/invalidroute and redirects it to /validlanguage
      path: `/:language(${possibleLanguages})/*`,
      redirect: to => {
        return `/${getValidLanguage()}`
      }
    },
    {
      // matches /invalidlanguage(2 chars)/anyroute and redirects it to /validlanguage/anyroute
      path: '/:language(\\w{2,2}\\b)/*',
      redirect: to => {
        return `/${getValidLanguage()}/${to.params[0]}`
      }
    },
    {
      // matches /anyroute (if not found) and redirects it to /validlanguage/anyroute
      path: '*',
      redirect: to => {
        return `/${getValidLanguage()}${to.path}`
      }
    }
  ])
}

export default routes
